import PropTypes from "prop-types";
import React, { useEffect, useMemo, useState } from "react";
import { BsDownload } from "react-icons/bs";
import { Button, Container, Table } from "react-bootstrap";
import { connect } from "react-redux";
import { GetKmSummary } from '../../../store/actions/reports/km-summary';
import { GetSubscribedVehiclesByCIAction } from '../../../store/actions/vehicle/vehicle.js';
import api from "../../../service/api";
import API_BOOK from "../../../service/endpoints";
import { exportToExcel } from "../../../utils/export-to-excel";
import Loader from "../../helpers/hoc/loader/Loader";
import MultiSelect from "../../helpers/hoc/multi-select/MultiSelect";
import NoRecordFound from "../../helpers/hoc/noRecordFound";
import Pagination from "../../helpers/hoc/paginator/Pagination";
import { useVehicles } from '../../../context/VehicleContext';
import Filters from '../new-report/report-charging/child-components/Filters';
import "./css/HistoricDetails.css";

const KmSummary = ({
  GetKmSummary,
  GetSubscribedVehiclesByCIAction,
  subscribedVehicles,
  allKmSummary,
  dataCount,
  isLoading,
}) => {
  const [startDate, setStartDate] = useState();
  const [endDate, setEndDate] = useState();
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [selectedOem, setSelectedOem] = useState([]);
  const [selectedModel, setSelectedModel] = useState([]);
  const [selectedCity, setSelectedCity] = useState([]);
  const [selectedVehicleType, setSelectedVehicleType] = useState([]);
  const [selectedHub, setSelectedHub] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [isRnFilterWarnEnabled, setIsRnFilterWarnEnabled] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [isDtFilterWarnEnabled, setIsDtFilterWarnEnabled] = useState(false);
  const [isSubmitClicked, setIsSubmitClicked] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 8;
  const { vehicles: mgVehicles } = useVehicles();

  const hubByRn = useMemo(() => {
    const map = {};
    (mgVehicles || []).forEach(v => {
      if (v.rn && v.mg?.depot_name) map[v.rn] = v.mg.depot_name;
    });
    return map;
  }, [mgVehicles]);

  // Load vehicles
  useEffect(() => {
    if (!subscribedVehicles?.length) {
      GetSubscribedVehiclesByCIAction();
    }
  }, [GetSubscribedVehiclesByCIAction, subscribedVehicles]);

  // Build filter options from vehicle data
  const filterOptions = useMemo(() => {
    const oems = [], models = [], cities = [], types = [], hubs = [];
    const oemSet = new Set(), modelSet = new Set(), citySet = new Set(), typeSet = new Set(), hubSet = new Set();
    (subscribedVehicles || []).forEach(v => {
      if (v.oem && !oemSet.has(v.oem)) { oemSet.add(v.oem); oems.push({ label: v.oem, value: v.oem }); }
      if (v.model && !modelSet.has(v.model)) { modelSet.add(v.model); models.push({ label: v.model, value: v.model }); }
      if (v.location && !citySet.has(v.location)) { citySet.add(v.location); cities.push({ label: v.location, value: v.location }); }
      const typeKey = v.type ? v.type.replace(/\s+/g, '').toLowerCase() : '';
      if (typeKey && !typeSet.has(typeKey)) { typeSet.add(typeKey); types.push({ label: v.type.trim(), value: typeKey }); }
      const hub = hubByRn[v.registration_number];
      if (hub && !hubSet.has(hub)) { hubSet.add(hub); hubs.push({ label: hub, value: hub }); }
    });
    return { oems, models, cities, types, hubs };
  }, [subscribedVehicles, hubByRn]);

  // Filter vehicles based on selected OEM/Model/City/Type and build RN options
  const filteredRnOptions = useMemo(() => {
    if (!subscribedVehicles?.length) return [];
    return subscribedVehicles
      .filter(v => {
        if (selectedOem.length && !selectedOem.some(o => o.value === v.oem)) return false;
        if (selectedModel.length && !selectedModel.some(m => m.value === v.model)) return false;
        if (selectedCity.length && !selectedCity.some(c => c.value === v.location)) return false;
        if (selectedVehicleType.length && !selectedVehicleType.some(t => t.value === (v.type || '').replace(/\s+/g, '').toLowerCase())) return false;
        if (selectedHub.length && !selectedHub.some(h => h.value === hubByRn[v.registration_number])) return false;
        return true;
      })
      .map(v => ({ label: v.registration_number, value: v._id }));
  }, [subscribedVehicles, selectedOem, selectedModel, selectedCity, selectedVehicleType, selectedHub, hubByRn]);

  const cityByRn = useMemo(() => {
    const map = {};
    (subscribedVehicles || []).forEach(v => { if (v.registration_number) map[v.registration_number] = v.location || ''; });
    return map;
  }, [subscribedVehicles]);

  // Auto-select all filtered vehicles when filters change
  useEffect(() => {
    if (filteredRnOptions.length > 0) {
      setSelectedOptions(filteredRnOptions);
    }
  }, [filteredRnOptions]);

  // Auto-fetch when vehicles selected and dates ready
  useEffect(() => {
    if (selectedOptions.length > 0 && startDate && endDate) {
      setCurrentPage(1);
      GetKmSummary({
        registrationNumbers: selectedOptions,
        fromDate: startDate,
        toDate: endDate,
        pageIndex: 0,
        pageSize: ITEMS_PER_PAGE,
      });
      setIsSubmitClicked(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOptions, startDate, endDate]);

  useEffect(() => {
    if (dataCount > 0) {
      GetKmSummary({
        registrationNumbers: selectedOptions,
        fromDate: startDate,
        toDate: endDate,
        pageIndex: currentPage - 1,
        pageSize: ITEMS_PER_PAGE,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  function toLocalDate(isoStr) {
    const d = new Date(isoStr);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }

  function onDateChangeHandler({ startDate, endDate }) {
    allKmSummary.length = 0;
    const from = toLocalDate(startDate);
    const to = toLocalDate(endDate);
    setStartDate(from);
    setEndDate(to);
    setCurrentPage(1);
  }

  function onSelectionChange() {
    allKmSummary.length = 0;
  }

  const onSubmitHandler = () => {
    if (!selectedOptions?.length) {
      setIsRnFilterWarnEnabled(true);
      setTimeout(() => { setIsRnFilterWarnEnabled(false); }, 2000);
      return;
    }
    if (!(endDate && startDate)) {
      setIsDtFilterWarnEnabled(true);
      setTimeout(() => { setIsDtFilterWarnEnabled(false); }, 2000);
      return;
    }
    setCurrentPage(1);
    GetKmSummary({
      registrationNumbers: selectedOptions,
      fromDate: startDate,
      toDate: endDate,
      pageIndex: 0,
      pageSize: ITEMS_PER_PAGE,
    });
    setTimeout(() => { setIsSubmitClicked(true); }, 1000);
  };

  const handleExportExcel = async () => {
    try {
      const { GET_KM_SUMMARY } = API_BOOK.REPORTS.KM_REPORT;
      const resp = await api({
        method: GET_KM_SUMMARY.method,
        url: GET_KM_SUMMARY.url,
        data: {
          registrationNumbers: selectedOptions?.map((val) => val.label),
          fromDate: startDate,
          toDate: endDate,
          pageSize: 0,
          pageIndex: 0,
        }
      });
      if (resp.status === 200 && resp?.data?.data?.rows?.length) {
        const rows = resp.data.data.rows;
        const headers = [
          { header: 'S.No', key: 'sno', width: 8 },
          { header: 'Registration No', key: 'rn', width: 18 },
          { header: 'OEM', key: 'oem', width: 12 },
          { header: 'City', key: 'city', width: 15 },
          { header: 'Chassis', key: 'cn', width: 20 },
          { header: 'Model', key: 'mdl', width: 15 },
          { header: 'Date', key: 'date', width: 12 },
          { header: 'Raw KM', key: 'rawKM', width: 12 },
          { header: 'Bad KM', key: 'badKM', width: 12 },
          { header: 'Clean KM', key: 'cleanKM', width: 12 },
          { header: 'Neg Bad Not Applied', key: 'negBadNotApplied', width: 18 },
          { header: 'Bad Pings', key: 'badPings', width: 12 },
          { header: 'Active Days', key: 'activeDays', width: 12 },
          { header: 'Valid Pings', key: 'validPings', width: 12 },
        ];
        const data = rows.map((n, i) => ({
          sno: i + 1,
          rn: n.rn || '-',
          oem: n.oem || '-',
          city: cityByRn[n.rn] || '-',
          cn: n.cn || '-',
          mdl: n.mdl || '-',
          date: n.date || '-',
          rawKM: n.rawKM != null ? Number(n.rawKM.toFixed(2)) : '-',
          badKM: n.badKM != null ? Number(n.badKM.toFixed(2)) : '-',
          cleanKM: n.cleanKM != null ? Number(n.cleanKM.toFixed(2)) : '-',
          negBadNotApplied: n.negBadNotApplied != null ? n.negBadNotApplied : '-',
          badPings: n.badPings != null ? n.badPings : '-',
          activeDays: n.activeDays != null ? n.activeDays : '-',
          validPings: n.validPings != null ? n.validPings : '-',
        }));
        exportToExcel({ data, headers, fileName: 'KM_Summary.xlsx', sheetName: 'KM Summary' });
      }
    } catch (error) {
      console.log('Export error:', error);
    }
  };

  return (
    <>
      <Container fluid>
        <div className="km-filter-row">
          <div className="km-filter-item">
            <MultiSelect
              isMultiSelect={true}
              options={filterOptions.oems}
              selectedOptions={selectedOem}
              setSelectedOptions={setSelectedOem}
              placeholder="OEM"
              placeholderColor='#C6C6C6'
            />
          </div>
          <div className="km-filter-item">
            <MultiSelect
              isMultiSelect={true}
              options={filterOptions.models}
              selectedOptions={selectedModel}
              setSelectedOptions={setSelectedModel}
              placeholder="Model"
              placeholderColor='#C6C6C6'
            />
          </div>
          <div className="km-filter-item">
            <MultiSelect
              isMultiSelect={true}
              options={filterOptions.cities}
              selectedOptions={selectedCity}
              setSelectedOptions={setSelectedCity}
              placeholder="City"
              placeholderColor='#C6C6C6'
            />
          </div>
          <div className="km-filter-item">
            <MultiSelect
              isMultiSelect={true}
              options={filterOptions.types}
              selectedOptions={selectedVehicleType}
              setSelectedOptions={setSelectedVehicleType}
              placeholder="Vehicle Type"
              placeholderColor='#C6C6C6'
            />
          </div>
          <div className="km-filter-item">
            <MultiSelect
              isMultiSelect={true}
              options={filterOptions.hubs}
              selectedOptions={selectedHub}
              setSelectedOptions={setSelectedHub}
              placeholder="Hub"
              placeholderColor='#C6C6C6'
            />
          </div>
        </div>

        <div className="km-action-row">
          <div className={`km-rn-select ${!selectedOptions[0] ? 'no-value-selected' : ''}`}>
            <MultiSelect
              isMultiSelect={true}
              setNoDataWarning={isRnFilterWarnEnabled}
              options={filteredRnOptions}
              selectedOptions={selectedOptions}
              setSelectedOptions={setSelectedOptions}
              onChange={onSelectionChange}
              placeholderColor='#C6C6C6'
              customBackgroundColor={{ backgroundColor: selectedOptions[0] ? "#FFFFFF" : "red" }}
            />
          </div>
          <div>
            <Button
              className="bttn height"
              id="getReport"
              type="submit"
              onClick={onSubmitHandler}
              disabled={!selectedOptions?.length || !endDate || !startDate}
            >
              Get Report
            </Button>
          </div>
          <div>
            <button
              disabled={!allKmSummary?.length || !selectedOptions?.length || !endDate || !startDate}
              className={
                !allKmSummary?.length || !selectedOptions?.length || !endDate || !startDate
                  ? "dwn-btn dwn-btn-disabled"
                  : "dwn-btn"
              }
              onClick={handleExportExcel}
            >
              <BsDownload style={{ width: '22px', height: '22px' }} />
            </button>
          </div>
          <div className='km-date-picker'>
            <Filters onDateChangeHandler={onDateChangeHandler} defaultDuration="yesterday"></Filters>
          </div>
        </div>

        {allKmSummary?.length ? (
          <div className="bg-white mb-2 mt-4 position-relative trip__log__table historic-details-table-wrapper">
            <Table responsive={true} borderless className="mb-0" id="km-summary-table">
              <thead>
                <tr className="th-border-bottom position-relative">
                  <th className="ps-2 text-nowrap"><p className="fw500">S.No</p></th>
                  <th className="ps-2 text-nowrap"><p className="fw500">Registration No</p></th>
                  <th className="ps-2 text-nowrap"><p className="fw500">OEM</p></th>
                  <th className="ps-2 text-nowrap"><p className="fw500">City</p></th>
                  <th className="ps-2 text-nowrap"><p className="fw500">Chassis</p></th>
                  <th className="ps-2 text-nowrap"><p className="fw500">Model</p></th>
                  <th className="ps-2 text-nowrap"><p className="fw500">Date</p></th>
                  <th className="ps-2 text-nowrap"><p className="fw500">Raw KM</p></th>
                  <th className="ps-2 text-nowrap"><p className="fw500">Bad KM</p></th>
                  <th className="ps-2 text-nowrap"><p className="fw500">Clean KM</p></th>
                  <th className="ps-2 text-nowrap"><p className="fw500">Neg Bad<br />Not Applied</p></th>
                  <th className="ps-2 text-nowrap"><p className="fw500">Bad Pings</p></th>
                  <th className="ps-2 text-nowrap"><p className="fw500">Active Days</p></th>
                  <th className="ps-2 text-nowrap"><p className="fw500">Valid Pings</p></th>
                </tr>
              </thead>
              <tbody>
                {allKmSummary?.map((n, index) => (
                  <tr key={index}>
                    <td>{index + 1 + ((currentPage - 1) * ITEMS_PER_PAGE) + "."}</td>
                    <td>{n?.rn || '-'}</td>
                    <td>{n?.oem || '-'}</td>
                    <td>{cityByRn[n?.rn] || '-'}</td>
                    <td>{n?.cn || '-'}</td>
                    <td>{n?.mdl || '-'}</td>
                    <td>{n?.date || '-'}</td>
                    <td>{n?.rawKM != null ? n.rawKM.toFixed(2) : '-'}</td>
                    <td>{n?.badKM != null ? n.badKM.toFixed(2) : '-'}</td>
                    <td>{n?.cleanKM != null ? n.cleanKM.toFixed(2) : '-'}</td>
                    <td>{n?.negBadNotApplied != null ? n.negBadNotApplied : '-'}</td>
                    <td>{n?.badPings != null ? n.badPings : '-'}</td>
                    <td>{n?.activeDays != null ? n.activeDays : '-'}</td>
                    <td>{n?.validPings != null ? n.validPings : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <Pagination
              disabled={false}
              totalPages={Math.ceil((dataCount || 0) / ITEMS_PER_PAGE)}
              currentPage={currentPage}
              setPage={setCurrentPage}
            />
          </div>
        ) : (
          <></>
        )}
        {!allKmSummary?.length && isSubmitClicked && !isLoading ? (
          <NoRecordFound></NoRecordFound>
        ) : (
          <></>
        )}
      </Container>
      <Loader isLoading={isLoading} />
    </>
  );
};

KmSummary.propTypes = {
  allKmSummary: PropTypes.array.isRequired,
  subscribedVehicles: PropTypes.array.isRequired,
  dataCount: PropTypes.number.isRequired,
  isLoading: PropTypes.bool.isRequired,
  GetKmSummary: PropTypes.func.isRequired,
  GetSubscribedVehiclesByCIAction: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  allKmSummary: state.kmSummary.allKmSummary,
  subscribedVehicles: state.vehicle.subscribedVehiclesByCI,
  dataCount: state.kmSummary.dataCount,
  isLoading: state.loader.isLoading,
});

export default connect(mapStateToProps, {
  GetKmSummary,
  GetSubscribedVehiclesByCIAction,
})(KmSummary);
